import { Controller } from "@hotwired/stimulus"

// Layer tree. Needed to reach nodes that are visually occluded or have no
// height on the canvas.
export default class extends Controller {
  static targets = ["list"]

  connect() {
    this.root = this.element.closest("[data-controller~='website-builder']")
    this.root.addEventListener("builder:ready", () => this.start(), { once: true })
    if (this.root.builder) this.start()
  }

  disconnect() { this.unsubscribe?.() }

  get builder() { return this.root.builder }
  get store() { return this.builder.store }

  start() {
    this.unsubscribe = this.store.subscribe((event) => {
      if (["structure", "document", "select"].includes(event.type)) this.render()
    })
    this.render()
  }

  render() {
    const rows = []
    this.walk(this.store.root, 0, rows)
    this.listTarget.replaceChildren(...rows)
  }

  walk(node, depth, rows) {
    rows.push(this.row(node, depth))
    ;(node.children || []).forEach((child) => this.walk(child, depth + 1, rows))
  }

  row(node, depth) {
    const labels = this.builder.schema.labels.types
    const selected = this.store.selectedId === node.id

    const button = document.createElement("button")
    button.type = "button"
    button.dataset.id = node.id
    button.dataset.type = node.type
    button.dataset.action = "builder-tree#select"
    button.style.paddingLeft = `${depth * 12 + 8}px`
    button.textContent = this.caption(node, labels)
    button.className =
      "block w-full truncate rounded px-2 py-1 text-left text-xs " +
      (selected
        ? "bg-cyan-100 font-semibold text-cyan-900 dark:bg-cyan-900/40 dark:text-cyan-100"
        : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800")

    if (node.id !== this.store.root.id) {
      button.draggable = true
      button.addEventListener("dragstart", (event) => this.onDragStart(event, node.id))
      button.addEventListener("dragover", (event) => this.onDragOver(event, button))
      button.addEventListener("dragleave", () => this.clearMarker(button))
      button.addEventListener("drop", (event) => this.onDrop(event, node.id, button))
      button.addEventListener("dragend", () => this.render())
    }

    return button
  }

  // --- reordering from the tree -------------------------------------------
  //
  // The tree is the only way to reach a node with no height on the canvas, so
  // it has to be able to move one as well as select it.

  onDragStart(event, id) {
    this.dragging = id
    event.dataTransfer.effectAllowed = "move"
    event.dataTransfer.setData("text/plain", id)
  }

  onDragOver(event, button) {
    if (!this.dragging || button.dataset.id === this.dragging) return

    event.preventDefault()
    const box = button.getBoundingClientRect()
    const after = event.clientY > box.top + box.height / 2
    button.classList.toggle("border-t-2", !after)
    button.classList.toggle("border-b-2", after)
    button.classList.add("border-cyan-500")
    button.dataset.dropAfter = after ? "true" : "false"
  }

  clearMarker(button) {
    button.classList.remove("border-t-2", "border-b-2", "border-cyan-500")
    delete button.dataset.dropAfter
  }

  onDrop(event, targetId, button) {
    event.preventDefault()
    const moving = this.dragging
    const after = button.dataset.dropAfter === "true"
    this.clearMarker(button)
    this.dragging = null
    if (!moving || moving === targetId) return

    const parent = this.store.parentOf(targetId)
    const node = this.store.node(moving)
    if (!parent || !node) return

    // Dropping onto a container the node can live in nests it; otherwise it
    // becomes a sibling of whatever it was dropped on.
    const target = this.store.node(targetId)
    const nests = (this.builder.schema.children[target.type] || []).includes(node.type)

    if (nests) {
      this.store.move(moving, targetId, (target.children || []).length)
      return
    }

    if (!(this.builder.schema.children[parent.type] || []).includes(node.type)) return

    this.store.move(moving, parent.id, this.store.indexOf(targetId) + (after ? 1 : 0))
  }

  // Prefer the node's own copy so the tree reads like the page.
  caption(node, labels) {
    const text = (node.props?.html || node.props?.label || "").replace(/<[^>]*>/g, "").trim()
    const type = labels[node.type] || node.type
    return text ? `${type} · ${text.slice(0, 24)}` : type
  }

  select(event) {
    this.store.select(event.currentTarget.dataset.id)
  }
}
